import React, { useState, useMemo } from 'react';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { Calendar, Filter, RefreshCw, Download, Settings, Eye, EyeOff, Zap, TrendingUp, Users, DollarSign } from 'lucide-react';
import { HelpButton } from '../Help/HelpButton';

interface DashboardFiltersProps {
  onExport: (format: 'pdf' | 'excel' | 'png') => Promise<void>;
  onRefresh: () => void;
  isLoading: boolean;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({ onExport, onRefresh, isLoading }) => {
  const { filters, setFilters, isRealTimeEnabled, setIsRealTimeEnabled, refreshInterval, setRefreshInterval } = useAnalytics();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const timeRangeOptions = React.useMemo(() => [
    { value: '7d', label: 'Last 7 days', days: 7 },
    { value: '30d', label: 'Last 30 days', days: 30 },
    { value: '90d', label: 'Last 3 months', days: 90 },
    { value: '1y', label: 'Last year', days: 365 },
    { value: 'custom', label: 'Custom', days: 0 }
  ], []);

  const batchLevelOptions = [
    { value: '1_day', label: 'Daily' },
    { value: '1_week', label: 'Weekly' },
    { value: '1_month', label: 'Monthly' },
    { value: '3_months', label: 'Quarterly' },
    { value: '6_months', label: 'Semi-annually' }
  ];

  const activityTypeOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'contributions', label: 'Contributions' },
    { value: 'benefits', label: 'Benefits' },
    { value: 'events', label: 'Events' },
    { value: 'expenses', label: 'Expenses' }
  ];

  const timeRange = useMemo(() => {
    if (filters.dateRange) {
      const start = new Date(filters.dateRange.start);
      const end = new Date(filters.dateRange.end);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      const found = timeRangeOptions.find(opt => Math.abs(opt.days - diffDays) < 2);
      return found ? found.value : 'custom';
    }
    return '30d';
  }, [filters.dateRange, timeRangeOptions]);

  const handleTimeRangeChange = React.useCallback((value: string) => {
    if (value === 'custom') {
      // Keep existing custom range
      return;
    }

    const option = timeRangeOptions.find(opt => opt.value === value);
    if (option && option.days > 0) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - option.days);

      setFilters({
        ...filters,
        dateRange: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      });
    }
  }, [filters, setFilters, timeRangeOptions]);

  const handleCustomDateChange = React.useCallback((field: 'start' | 'end', value: string) => {
    setFilters({
      ...filters,
      dateRange: {
        ...filters.dateRange!,
        [field]: new Date(value).toISOString()
      }
    });
  }, [filters, setFilters]);

  const refreshIntervalOptions = [
    { value: 10000, label: '10 seconds' },
    { value: 30000, label: '30 seconds' },
    { value: 60000, label: '1 minute' },
    { value: 300000, label: '5 minutes' },
    { value: 900000, label: '15 minutes' }
  ];

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Dashboard Filters</h3>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <Settings className="h-4 w-4" />
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </button>

          <HelpButton guideId="dashboard/filter-functionality" variant="icon" />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onRefresh()}
            disabled={isLoading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg transition-colors"
            aria-label="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onExport('pdf')}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors"
              aria-label="Export as PDF"
            >
              <Download className="h-4 w-4" />
              <span>PDF</span>
            </button>

            <button
              onClick={() => onExport('excel')}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-colors"
              aria-label="Export as Excel"
            >
              <Download className="h-4 w-4" />
              <span>Excel</span>
            </button>

            <button
              onClick={() => onExport('png')}
              className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors"
              aria-label="Export as PNG"
            >
              <Download className="h-4 w-4" />
              <span>PNG</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Time Range Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Time Range</label>
          <select
            value={timeRange}
            onChange={(e) => handleTimeRangeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {timeRangeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Batch Level Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Aggregation Level</label>
          <select
            value={filters.batchLevel || '1_week'}
            onChange={(e) => setFilters({ ...filters, batchLevel: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {batchLevelOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Activity Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Activity Type</label>
          <select
            value={filters.activityType || 'all'}
            onChange={(e) => setFilters({ ...filters, activityType: e.target.value as any })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {activityTypeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Real-time Toggle */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Real-time Updates</label>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsRealTimeEnabled(!isRealTimeEnabled)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${isRealTimeEnabled
                ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                : 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700'
                }`}
            >
              {isRealTimeEnabled ? (
                <>
                  <Zap className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>Enabled</span>
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>Disabled</span>
                </>
              )}
            </button>

            {isRealTimeEnabled && (
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {refreshIntervalOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Custom Date Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Start Date</label>
              <input
                type="datetime-local"
                value={filters.dateRange?.start ? new Date(filters.dateRange.start).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleCustomDateChange('start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom End Date</label>
              <input
                type="datetime-local"
                value={filters.dateRange?.end ? new Date(filters.dateRange.end).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleCustomDateChange('end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Summary */}
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 text-sm rounded-full border border-blue-200 dark:border-blue-800">
          <Calendar className="h-3 w-3" />
          {filters.dateRange ? `${new Date(filters.dateRange.start).toLocaleDateString()} - ${new Date(filters.dateRange.end).toLocaleDateString()}` : 'Custom range'}
        </span>

        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 text-sm rounded-full border border-purple-200 dark:border-purple-800">
          <TrendingUp className="h-3 w-3" />
          {filters.batchLevel || 'Weekly'}
        </span>

        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 text-sm rounded-full border border-green-200 dark:border-green-800">
          <Users className="h-3 w-3" />
          {filters.activityType === 'all' ? 'All activities' : filters.activityType}
        </span>

        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 text-sm rounded-full border border-yellow-200 dark:border-yellow-800">
          <DollarSign className="h-3 w-3" />
          {isRealTimeEnabled ? `Live (${Math.round(refreshInterval / 1000)}s)` : 'Manual refresh'}
        </span>
      </div>
    </div>
  );
};